import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { ShopPack } from '../../../../models/shop/shop-pack.model';
import { ShopPacksFiltersRequest } from '../../../../models/shop/requests/api-shop-get-packs-request.model';
import { ApiShopService } from '../../../../services/shop/api/api-shop.service';
import { ShopService } from '../../../../services/shop/shop.service';
import { AlertsService } from '../../../../services/alerts.service';
import { ModalsService } from '../../../../services/modals.service';
import { DateService } from '../../../../services/date.service';
import { ShopPackAddModalComponent } from '../../modals/shop-pack-add-modal/shop-pack-add-modal.component';
import { ShopPackEditModalComponent } from '../../modals/shop-pack-edit-modal/shop-pack-edit-modal.component';

@Component({
  selector: 'app-shop-packs-tab',
  templateUrl: './shop-packs-tab.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, TooltipModule, CollapseModule, ShopPackAddModalComponent, ShopPackEditModalComponent]
})
export class ShopPacksTabComponent implements OnInit {

  @ViewChild('addModal') addModal!: ShopPackAddModalComponent;
  @ViewChild('editModal') editModal!: ShopPackEditModalComponent;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  packs: ShopPack[] = [];
  isLoading: boolean = false;
  totalResults: number = 0;
  readonly resultsPerPage: number = 25;

  isFilterOpen: boolean = false;
  filters: ShopPacksFiltersRequest = new ShopPacksFiltersRequest();
  startDateMinFilter: string = '';
  startDateMaxFilter: string = '';

  private static pageNumber: number = 1;

  get pageNumber(): number { return ShopPacksTabComponent.pageNumber; }

  get totalPages(): number { return Math.ceil(this.totalResults / this.resultsPerPage); }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  goToPage(page: number): void { ShopPacksTabComponent.pageNumber = page; this.refreshPacks(); }
  onClickPaginationPrevious(): void { ShopPacksTabComponent.pageNumber--; this.refreshPacks(); }
  onClickPaginationNext(): void { ShopPacksTabComponent.pageNumber++; this.refreshPacks(); }

  constructor(
    public dateService: DateService,
    public shopService: ShopService,
    private apiShopService: ApiShopService,
    private alertsService: AlertsService,
    private modalsService: ModalsService
  ) {}

  ngOnInit(): void {}

  refresh(): void { this.refreshPacks(); }

  onClickFilter(): void {
    this.filters.start_date_min_timestamp = this.startDateMinFilter ? Math.floor(new Date(this.startDateMinFilter).getTime() / 1000) : null;
    this.filters.start_date_max_timestamp = this.startDateMaxFilter ? Math.floor(new Date(this.startDateMaxFilter).getTime() / 1000) : null;
    ShopPacksTabComponent.pageNumber = 1;
    this.refreshPacks();
  }

  onClickResetFilters(): void {
    this.filters = new ShopPacksFiltersRequest();
    this.startDateMinFilter = '';
    this.startDateMaxFilter = '';
    ShopPacksTabComponent.pageNumber = 1;
    this.refreshPacks();
  }

  getItemName(itemId: number): string {
    return this.shopService.items.find(i => i.itemId === itemId)?.name ?? String(itemId);
  }

  getPackTitle(pack: ShopPack): string {
    return pack.name || pack.translations.find(t => t.title)?.title || '';
  }

  getPackStatus(pack: ShopPack): 'waiting' | 'in_progress' | 'past' {
    const now = Math.floor(Date.now() / 1000);
    if (now < pack.dateStartTimestamp) return 'waiting';
    if (now <= pack.dateEndTimestamp) return 'in_progress';
    return 'past';
  }

  onClickAdd(): void {
    this.addModal.show(() => this.refreshPacks());
  }

  onClickEdit(pack: ShopPack): void {
    this.editModal.show(pack, () => this.refreshPacks());
  }

  onClickAddFromFile(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.isLoading = true;
    this.apiShopService.addPacksFromFile(file).then(
      () => {
        this.alertsService.displayAlertSuccess('Packs imported successfully!');
        this.refreshPacks();
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error importing packs: ' + error);
      }
    );
    input.value = '';
  }

  onClickDelete(pack: ShopPack): void {
    this.modalsService.showConfirmModal(() => {
      this.apiShopService.deletePack(pack.packId).then(
        () => {
          this.alertsService.displayAlertSuccess('Pack deleted !');
          this.refreshPacks();
        }, error => {
          this.alertsService.displayAlertError('Error deleting pack : ' + error);
        }
      );
    });
  }

  onClickDeleteNextPacks(): void {
    this.modalsService.showConfirmModal(() => {
      this.isLoading = true;
      this.apiShopService.deleteNextPacks().then(
        () => {
          this.alertsService.displayAlertSuccess('Upcoming packs deleted !');
          this.refreshPacks();
        }, error => {
          this.isLoading = false;
          this.alertsService.displayAlertError('Error deleting upcoming packs : ' + error);
        }
      );
    });
  }

  private refreshPacks(): void {
    this.isLoading = true;
    this.apiShopService.getPacks(this.pageNumber, this.resultsPerPage, this.filters).then(
      response => {
        this.isLoading = false;
        this.packs = response.results;
        this.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading packs : ' + error);
      }
    );
  }
}
